import { RefObject, useMemo, useRef } from "react";
import { Camera, Object3D, WebGLRenderer } from "three";

import type { FrameCallback } from "./Renderer";

const kDefaultCanvasSize = 64;

export type SceneAndCamera = { scene: Object3D | null; camera: Camera | null };

class SecondaryRenderer {
  readonly srcCanvas = new OffscreenCanvas(
    kDefaultCanvasSize,
    kDefaultCanvasSize
  );
  readonly webgl = new WebGLRenderer({
    powerPreference: "high-performance",
    canvas: this.srcCanvas,
    alpha: true,
    antialias: false
  });

  constructor() {
    this.webgl.setPixelRatio(window.devicePixelRatio);
    this.webgl.shadowMap.enabled = false;
  }
}

class SecondaryRenderCanvasBinding {
  private renderer: SecondaryRenderer;
  private canvasRef: RefObject<HTMLCanvasElement | null>;
  private bitmapCtx?: ImageBitmapRenderingContext;
  private scene?: Object3D;
  private camera?: Camera;
  private animationFrameHandle: number | null = null;
  private lastFrameTime: number | null = null;

  constructor(
    renderer: SecondaryRenderer,
    canvasRef: RefObject<HTMLCanvasElement | null>
  ) {
    this.renderer = renderer;
    this.canvasRef = canvasRef;
  }

  setScene(scene: Object3D) {
    this.scene = scene;
    return this;
  }

  setCamera(camera: Camera) {
    this.camera = camera;
    return this;
  }

  renderFrame() {
    if (!this.canvasRef.current || !this.scene || !this.camera) return;
    // NOTE: get width/height directly to make the icons in submenus render
    const { width, height } = this.canvasRef.current;
    if (!width || !height) return;
    this.renderer.webgl.setSize(width, height, false);
    this.renderer.webgl.render(this.scene, this.camera);
    const ctx =
      this.bitmapCtx ?? this.canvasRef.current.getContext("bitmaprenderer");
    if (!ctx) throw new Error("Unable to get bitmap context for canvas!");
    this.bitmapCtx = ctx;
    ctx.transferFromImageBitmap(
      this.renderer.srcCanvas.transferToImageBitmap()
    );
  }

  startRenderLoop(onFrame: FrameCallback) {
    this.stopRenderLoop();

    const tick = (time: number) => {
      const deltaSeconds =
        this.lastFrameTime === null ? 0 : (time - this.lastFrameTime) / 1000;

      this.lastFrameTime = time;

      onFrame(deltaSeconds);
      this.renderFrame();

      this.animationFrameHandle = requestAnimationFrame(tick);
    };

    this.animationFrameHandle = requestAnimationFrame(tick);
  }

  stopRenderLoop() {
    if (this.animationFrameHandle !== null)
      cancelAnimationFrame(this.animationFrameHandle);

    this.animationFrameHandle = null;
    this.lastFrameTime = null;
  }
}

const kSecondaryRendererGlobal = new SecondaryRenderer();

export function useSecondaryRenderer(): [
  RefObject<HTMLCanvasElement | null>,
  SecondaryRenderCanvasBinding
] {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const binding = useMemo(
    () => new SecondaryRenderCanvasBinding(kSecondaryRendererGlobal, canvasRef),
    []
  );
  return [canvasRef, binding];
}
